import { idbAll, idbDelete, idbGet, idbPut, openDb } from '../../lib/idb';
import type { OfflineVerificationKey } from '../../api/types';

/**
 * Офлайн-очередь чек-инов и локальное хранение ключа офлайн-проверки.
 * Синхронизация выполняется при появлении сети (FR-PASS-01).
 */

const DB_NAME = 'promtver-partner';
const DB_VERSION = 1;
const QUEUE_STORE = 'checkin-queue';
const KV_STORE = 'kv';

export interface PendingCheckin {
  /** Идентификатор события — обеспечивает идемпотентность при синхронизации. */
  clientEventId: string;
  code: string;
  source: 'qr' | 'manual';
  createdAt: string;
  attempts: number;
  lastError?: string | null;
}

export interface StoredOfflineKey extends OfflineVerificationKey {
  /** Ключ записи в хранилище «ключ — значение». */
  storageKey: string;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function db(): Promise<IDBDatabase> {
  if (!dbPromise) {
    dbPromise = openDb(DB_NAME, DB_VERSION, (database) => {
      if (!database.objectStoreNames.contains(QUEUE_STORE)) {
        database.createObjectStore(QUEUE_STORE, { keyPath: 'clientEventId' });
      }
      if (!database.objectStoreNames.contains(KV_STORE)) {
        database.createObjectStore(KV_STORE, { keyPath: 'storageKey' });
      }
    });
  }
  return dbPromise;
}

export async function enqueueCheckin(item: PendingCheckin): Promise<void> {
  const database = await db();
  await idbPut(database, QUEUE_STORE, item);
}

export async function listPendingCheckins(): Promise<PendingCheckin[]> {
  const database = await db();
  const items = await idbAll<PendingCheckin>(database, QUEUE_STORE);
  return items.sort((a, b) => a.createdAt.localeCompare(b.createdAt));
}

export async function removePendingCheckin(clientEventId: string): Promise<void> {
  const database = await db();
  await idbDelete(database, QUEUE_STORE, clientEventId);
}

export async function countPendingCheckins(): Promise<number> {
  const database = await db();
  const items = await idbAll<PendingCheckin>(database, QUEUE_STORE);
  return items.length;
}

/** Ключ офлайн-проверки хранится локально и не покидает устройство. */
export async function saveOfflineKey(key: OfflineVerificationKey): Promise<void> {
  const database = await db();
  await idbPut(database, KV_STORE, { ...key, storageKey: 'offline-verification-key' } satisfies StoredOfflineKey);
}

export async function loadOfflineKey(): Promise<StoredOfflineKey | undefined> {
  const database = await db();
  return idbGet<StoredOfflineKey>(database, KV_STORE, 'offline-verification-key');
}

export async function clearOfflineKey(): Promise<void> {
  const database = await db();
  await idbDelete(database, KV_STORE, 'offline-verification-key');
}
